import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  applyIfLatest,
  latestRequestInitialState,
  trackLatestRequests,
} from 'state/latestRequest';

import {
  changeOwnPassword,
  createAdmin,
  deleteAdmin,
  getAdmins,
  resetAdminPassword,
} from 'state/thunks/adminThunk/adminThunk';

const initialState = {
  admins: [] as any[],
  isLoading: false,
  // Errors are kept here rather than in authSlice so a failed "add admin" does
  // not light up the login form's error line.
  error: '' as string,
  ...latestRequestInitialState,
};

/**
 * The filtered list read is guarded by `applyIfLatest`: a search string, a
 * status filter and a page all change it, so overlapping requests are routine
 * and the reducer must apply the newest REQUEST rather than the newest
 * response. See src/state/latestRequest.ts.
 */
const slice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setLogOut: () => initialState,
    clearError: state => {
      state.error = '';
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<any>) => {
    builder
      .addCase(getAdmins.pending, state => {
        state.isLoading = true;
      })
      .addCase(
        getAdmins.fulfilled,
        applyIfLatest(getAdmins, (state, payload: any) => {
        state.isLoading = false;
        // The endpoint returns a bare array; guard anyway so an unexpected
        // envelope cannot leave `admins` non-iterable and crash the table.
        state.admins = Array.isArray(payload) ? payload : [];
        }),
      )
      .addCase(getAdmins.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(createAdmin.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
      })
      .addCase(deleteAdmin.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
      })
      .addCase(resetAdminPassword.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
      })
      .addCase(changeOwnPassword.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
      });

    // Matchers must come after every addCase.
    trackLatestRequests(builder, [getAdmins]);
  },
});

export const adminActions = slice.actions;

export const adminReducer = slice.reducer;
